import React, { useEffect, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

function Mini({ title, value, subtitle, color = "#2563eb" }) {
  return (
    <div style={{ background: "#f9fafb", padding: "10px 12px", borderRadius: "10px" }}>
      <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "2px" }}>{title}</div>
      <div style={{ fontSize: "18px", fontWeight: 700, color }}>{value}</div>
      {subtitle && <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "2px" }}>{subtitle}</div>}
    </div>
  );
}

function fmtMoney(n) {
  return (Number(n) || 0).toLocaleString("ru-RU") + " ₽";
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminUserModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
          credentials: "include",
        });
        if (res.status === 403) {
          setError("Доступ только для администратора");
          return;
        }
        if (!res.ok) throw new Error("Ошибка загрузки");
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  // Закрытие по Esc
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: "20px", backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white", borderRadius: "16px", maxWidth: "960px",
          width: "100%", maxHeight: "90vh", overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "16px 24px", borderBottom: "1px solid #e5e7eb",
          position: "sticky", top: 0, background: "white", zIndex: 10,
        }}>
          <h2 style={{ fontSize: "18px", fontWeight: 700 }}>
            👤 Карточка юзера #{userId}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "none", fontSize: "24px",
              cursor: "pointer", color: "#9ca3af", lineHeight: 1,
            }}
            title="Закрыть (Esc)"
          >×</button>
        </div>

        {/* Content */}
        <div style={{ padding: "20px 24px" }}>
          {loading && <div style={{ textAlign: "center", color: "#6b7280", padding: 40 }}>Загрузка...</div>}
          {error && <div style={{ textAlign: "center", color: "#ef4444", padding: 40 }}>{error}</div>}

          {data && (
            <>
              {/* Профиль */}
              <div style={{
                background: "#f9fafb", borderRadius: "12px",
                padding: "16px", marginBottom: "20px",
              }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: "20px", fontWeight: 700 }}>{data.user.name || "—"}</div>
                    <div style={{ fontSize: "14px", color: "#6b7280", marginTop: "2px" }}>{data.user.email}</div>
                  </div>
                  <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: "11px", color: "#9ca3af" }}>Тариф</div>
                      <div style={{
                        fontWeight: 600,
                        color: data.user.plan === "free" ? "#6b7280" : "#16a34a",
                      }}>
                        {data.user.plan}
                        {data.user.plan_active && " ✓"}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: "11px", color: "#9ca3af" }}>Email подтверждён</div>
                      <div style={{ fontWeight: 600 }}>{data.user.email_verified ? "✓ Да" : "❌ Нет"}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "11px", color: "#9ca3af" }}>Роль</div>
                      <div style={{ fontWeight: 600 }}>{data.user.role || "user"}</div>
                    </div>
                  </div>
                </div>
                <div style={{
                  display: "flex", gap: "20px", flexWrap: "wrap",
                  marginTop: "12px", fontSize: "12px", color: "#6b7280",
                }}>
                  <span>📅 Регистрация: {fmtDate(data.user.created_at)}</span>
                  <span>🕐 Последний вход: {fmtDate(data.user.last_login_at)}</span>
                  {data.user.plan_expires && (
                    <span>⏳ Тариф до: {fmtDate(data.user.plan_expires)}</span>
                  )}
                </div>
              </div>

              {/* Карточки */}
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "10px", marginBottom: "20px",
              }}>
                <Mini title="SMTP" value={data.smtp.count} color="#3b82f6" />
                <Mini title="Баз" value={data.databases.count}
                      subtitle={`${data.databases.totalContacts} контактов`} />
                <Mini title="Кампаний" value={data.campaigns.total} />
                <Mini title="Одобрено" value={data.campaigns.approved} color="#16a34a" />
                <Mini title="На модерации" value={data.campaigns.pending} color="#f59e0b" />
                <Mini title="Отклонено" value={data.campaigns.rejected} color="#ef4444" />
                <Mini title="Отправлено" value={data.campaigns.total_sent} color="#16a34a" />
                <Mini title="Ошибок" value={data.campaigns.total_failed} color="#ef4444" />
              </div>

              {/* График активности за 30 дней */}
              {data.daily_activity.length > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <h3 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "10px" }}>
                    📈 Активность за 30 дней
                  </h3>
                  <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "12px", height: "240px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.daily_activity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip contentStyle={{ fontSize: 12 }} />
                        <Line type="monotone" dataKey="sent" stroke="#22c55e" name="Отправлено" strokeWidth={2} dot={{ r: 2 }} />
                        <Line type="monotone" dataKey="failed" stroke="#ef4444" name="Ошибок" strokeWidth={2} dot={{ r: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* SMTP */}
              <h3 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "10px" }}>
                📧 SMTP-аккаунты ({data.smtp.count})
              </h3>
              <div style={{ background: "white", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden", marginBottom: "20px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ background: "#f9fafb", color: "#6b7280" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left" }}>Email</th>
                      <th style={{ padding: "8px 12px", textAlign: "left" }}>Host</th>
                      <th style={{ padding: "8px 12px", textAlign: "left" }}>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.smtp.accounts.map(s => (
                      <tr key={s.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 12px" }}>{s.email}</td>
                        <td style={{ padding: "8px 12px", color: "#6b7280", fontFamily: "monospace" }}>
                          {s.host}:{s.port}
                        </td>
                        <td style={{ padding: "8px 12px" }}>
                          <span style={{
                            padding: "2px 8px", borderRadius: "6px", fontSize: "11px",
                            background: s.status === "active" ? "#dcfce7" : "#f3f4f6",
                            color: s.status === "active" ? "#166534" : "#6b7280",
                          }}>{s.status || "—"}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.smtp.accounts.length === 0 && (
                  <div style={{ padding: 20, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                    Нет SMTP-аккаунтов
                  </div>
                )}
              </div>

              {/* Базы */}
              <h3 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "10px" }}>
                📁 Базы контактов ({data.databases.count})
              </h3>
              <div style={{ background: "white", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden", marginBottom: "20px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ background: "#f9fafb", color: "#6b7280" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left" }}>Название</th>
                      <th style={{ padding: "8px 12px", textAlign: "right" }}>Контактов</th>
                      <th style={{ padding: "8px 12px", textAlign: "right" }}>Создана</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.databases.list.map(d => (
                      <tr key={d.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 12px" }}>{d.name}</td>
                        <td style={{ padding: "8px 12px", textAlign: "right" }}>{d.contacts_count}</td>
                        <td style={{ padding: "8px 12px", textAlign: "right", color: "#6b7280" }}>{fmtDate(d.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.databases.list.length === 0 && (
                  <div style={{ padding: 20, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                    Нет баз
                  </div>
                )}
              </div>

              {/* Платежи */}
              <h3 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "10px" }}>
                💰 Платежи
              </h3>
              <div style={{ background: "white", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ background: "#f9fafb", color: "#6b7280" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left" }}>Сумма</th>
                      <th style={{ padding: "8px 12px", textAlign: "left" }}>План</th>
                      <th style={{ padding: "8px 12px", textAlign: "left" }}>Статус</th>
                      <th style={{ padding: "8px 12px", textAlign: "right" }}>Оплачен</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payments.map(p => (
                      <tr key={p.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 12px", fontWeight: 600 }}>{fmtMoney(p.amount)}</td>
                        <td style={{ padding: "8px 12px" }}>{p.tariff || "—"}</td>
                        <td style={{ padding: "8px 12px" }}>
                          <span style={{
                            padding: "2px 8px", borderRadius: "6px", fontSize: "11px",
                            background: p.status === "paid" ? "#dcfce7" : "#fef3c7",
                            color: p.status === "paid" ? "#166534" : "#92400e",
                          }}>{p.status}</span>
                        </td>
                        <td style={{ padding: "8px 12px", textAlign: "right", color: "#6b7280" }}>
                          {fmtDate(p.paid_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.payments.length === 0 && (
                  <div style={{ padding: 20, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                    Платежей пока нет
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}